import { FormType, useGetFormSubmissionsQuery } from '@/__generated__/types';
import type { FormSubmissionTableItem } from '@/components/_dashboard/forms/FormsTable';
import { FormsTable } from '@/components/_dashboard/forms/FormsTable';
import { formatFormSubmissionDate, getFormFieldValue, POS_FORM_FIELD_LABELS } from '@/components/_dashboard/forms/formUtils';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import Page from '@/components/Page';
import { FORM_TYPE_LABELS, FORM_TYPE_OPTIONS } from '@/constants/form';
import useSettings from '@/hooks/useSettings';
import { PATH_DASHBOARD } from '@/routes/paths';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, Container, MenuItem, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PublicFormLinkCard from './PublicFormLinkCard';

export default function FormList(): JSX.Element {
    const { themeStretch } = useSettings();
    const { t } = useTranslation();
    const [formTypeFilter, setFormTypeFilter] = useState<FormType | ''>('');
    const { data, loading, error, refetch } = useGetFormSubmissionsQuery({
        fetchPolicy: 'cache-and-network'
    });

    const submissionList = useMemo<FormSubmissionTableItem[]>(() => {
        return (data?.getFormSubmissions ?? []).map((submission) => ({
            id: submission.id,
            formType: submission.formType,
            formTypeLabel: t(`form.types.${submission.formType}`, FORM_TYPE_LABELS[submission.formType]),
            restaurantName: getFormFieldValue(submission.fields, POS_FORM_FIELD_LABELS.restaurantName),
            contactName: getFormFieldValue(submission.fields, POS_FORM_FIELD_LABELS.contactName),
            mobileNumber: getFormFieldValue(submission.fields, POS_FORM_FIELD_LABELS.mobileNumber),
            email: getFormFieldValue(submission.fields, POS_FORM_FIELD_LABELS.email),
            city: getFormFieldValue(submission.fields, POS_FORM_FIELD_LABELS.city),
            businessType: getFormFieldValue(submission.fields, POS_FORM_FIELD_LABELS.businessType),
            referralSource: getFormFieldValue(submission.fields, POS_FORM_FIELD_LABELS.referralSource),
            message: getFormFieldValue(submission.fields, POS_FORM_FIELD_LABELS.message),
            status: submission.status,
            createdAt: submission.createdAt ?? null,
            createdAtDisplay: formatFormSubmissionDate(submission.createdAt),
            updatedAt: submission.updatedAt ?? null,
            updatedAtDisplay: formatFormSubmissionDate(submission.updatedAt)
        }));
    }, [data, t]);

    const filteredSubmissionList = useMemo<FormSubmissionTableItem[]>(() => {
        if (!formTypeFilter) return submissionList;

        return submissionList.filter((submission) => submission.formType === formTypeFilter);
    }, [formTypeFilter, submissionList]);

    const customerHeader = (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
            <TextField
                select
                size="small"
                label="Enquiry Type"
                value={formTypeFilter}
                onChange={(event) => setFormTypeFilter(event.target.value as FormType | '')}
                sx={{ minWidth: 240 }}
            >
                <MenuItem value="">All enquiry types</MenuItem>
                {FORM_TYPE_OPTIONS.map((formType) => (
                    <MenuItem key={formType} value={formType}>
                        {t(`form.types.${formType}`, FORM_TYPE_LABELS[formType])}
                    </MenuItem>
                ))}
            </TextField>
            <Button
                variant="outlined"
                color="inherit"
                startIcon={<RefreshIcon />}
                disabled={loading}
                onClick={() => {
                    void refetch();
                }}
            >
                Refresh
            </Button>
        </Box>
    );

    return (
        <Page title={t('Forms')}>
            <Container maxWidth={themeStretch ? false : 'lg'}>
                <HeaderBreadcrumbs
                    heading={t('Forms')}
                    links={[
                        { name: t('Dash Board'), href: PATH_DASHBOARD.root },
                        { name: t('Forms'), href: PATH_DASHBOARD.forms.root },
                        { name: t('POS') }
                    ]}
                />
                <PublicFormLinkCard />
                {error && <Alert severity="error">Failed to load form submissions.</Alert>}
                {!error && (
                    <FormsTable
                        submissionList={filteredSubmissionList}
                        isLoading={loading && !data}
                        onStatusUpdated={() => {
                            void refetch();
                        }}
                        customerHeader={customerHeader}
                    />
                )}
            </Container>
        </Page>
    );
}
